/**
 * Instancia única del cliente de la Fitness API para toda la web.
 *
 * El token de refresco vive en la cookie `httpOnly` `fit_refresh` que pone el
 * propio servidor (`authTransport: 'cookie'`): ningún script, tampoco uno
 * inyectado por un XSS, puede leerlo. El de acceso está solo en memoria, así
 * que una recarga lo pierde y `AuthProvider` recupera la sesión pidiendo un
 * refresco con la cookie. En `localStorage` queda únicamente un indicador sin
 * secretos (`SESSION_HINT_KEY`) para saber, sin red, que había alguien dentro.
 * Ver `apps/web/README.md` y la sección «Sesión de la web» de `backend/README.md`.
 */
import {
  ApiClientError,
  MemoryTokenStorage,
  NetworkError,
  createApiClient,
  isSession,
  type Session,
  type User,
} from '@fitness/api-client';

import { createAccountFetch } from './accountFetch';

/**
 * Clave donde la web guardaba antes la sesión entera, token de refresco
 * incluido. Solo se lee al arrancar para migrarla a la cookie (ver
 * `takeLegacySession`) y después se borra.
 */
export const LEGACY_SESSION_STORAGE_KEY = 'fitness.session';

/**
 * Indicador de «hay sesión»: la cuenta con la que se entró por última vez. No
 * lleva ningún token, solo lo mismo que ya guarda en disco el service worker
 * al cachear `GET /v1/me`, y sirve para abrir la app sin red con los datos
 * locales.
 */
export const SESSION_HINT_KEY = 'fitness.user';

/** Raíz de la API; se configura con `VITE_API_URL` (ver `.env.example`). */
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

type SessionListener = (session: Session | null) => void;

const sessionListeners = new Set<SessionListener>();
let signedOutHandler: (() => void) | null = null;

/**
 * Se notifica cada vez que el cliente guarda o borra la sesión (login, logout,
 * refresco o expulsión). `AuthProvider` se suscribe para mantener su estado.
 */
export function onSessionChange(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

/**
 * Registra qué hacer cuando la sesión se pierde de forma involuntaria (el
 * refresco fue rechazado). Lo instala `AuthProvider` para limpiar y llevar a
 * `/login` sin recargar la página.
 */
export function setSignedOutHandler(handler: (() => void) | null): void {
  signedOutHandler = handler;
}

/** Sesión en memoria: el token de acceso no se escribe nunca en disco. */
export const memorySession = new MemoryTokenStorage();

/**
 * Un refresco cada vez entre todas las pestañas. Cada una tiene su propio
 * token de acceso en memoria, pero la cookie es la misma: si dos la canjean a
 * la vez, la segunda llega con un token ya rotado y el servidor cierra todas
 * las sesiones. Con el cerrojo, la segunda espera y sale ya con la cookie nueva.
 */
async function oneRefreshAcrossTabs<T>(refresh: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return await navigator.locks.request('fitness.auth.refresh', refresh);
  }
  return refresh();
}

/**
 * Ninguna petición con el token de una cuenta sale mientras la pestaña sirve
 * los datos de otra (ver `accountFetch.ts`). Si llega el de otra cuenta con
 * una abierta —otra pestaña ha cambiado de cuenta y la cookie ya es suya—,
 * esta pestaña cierra su sesión en vez de mezclar las dos.
 */
const accountFetch = createAccountFetch(undefined, () => {
  void memorySession.clear();
  signedOutHandler?.();
});

/**
 * Ata el cliente a la cuenta cuya base local está abierta, o a ninguna al
 * cerrar sesión. Corta las peticiones con token que seguían en vuelo.
 */
export function bindApiAccount(userId: string | null): void {
  accountFetch.bind(userId);
}

export const api = createApiClient({
  baseUrl: API_BASE_URL,
  storage: memorySession,
  authTransport: 'cookie',
  fetch: accountFetch.fetch,
  serializeRefresh: oneRefreshAcrossTabs,
  onSessionChange(session) {
    for (const listener of sessionListeners) listener(session);
  },
  onSignedOut() {
    signedOutHandler?.();
  },
});

// --- localStorage -----------------------------------------------------------
// Todo acceso va envuelto: el navegador puede negarlo (modo privado, datos de
// sitio bloqueados) y la app tiene que seguir funcionando sin él.

function readItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Sin indicador la app solo pierde el arranque sin red.
  }
}

function removeItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ídem.
  }
}

/**
 * Sesión antigua de `localStorage` con su token de refresco, si la hay. No la
 * borra: eso lo hace `clearLegacySession` cuando el servidor ya la ha
 * cambiado por la cookie (o la ha rechazado), para no perderla si el primer
 * arranque tras actualizar ocurre sin red.
 */
export function takeLegacySession(): Session | null {
  const raw = readItem(LEGACY_SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isSession(parsed) && parsed.refreshToken) return parsed;
  } catch {
    // Ilegible: no hay nada que migrar.
  }
  removeItem(LEGACY_SESSION_STORAGE_KEY);
  return null;
}

export function clearLegacySession(): void {
  removeItem(LEGACY_SESSION_STORAGE_KEY);
}

/** La cuenta del indicador de sesión, o `null`. */
export function readSessionHint(): User | null {
  const raw = readItem(SESSION_HINT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<User> | null;
    if (parsed && typeof parsed.id === 'string' && typeof parsed.handle === 'string') {
      return parsed as User;
    }
  } catch {
    // Ilegible: como si no hubiera.
  }
  removeItem(SESSION_HINT_KEY);
  return null;
}

export function writeSessionHint(user: User): void {
  writeItem(SESSION_HINT_KEY, JSON.stringify(user));
}

export function clearSessionHint(): void {
  removeItem(SESSION_HINT_KEY);
}

/**
 * Traduce cualquier error lanzado por el cliente a un texto claro: qué ha
 * pasado y qué hacer. El mapeo vive en `@fitness/core` (ver `lib/errors.ts`).
 */
export { describeError } from './errors';

/** `true` si el error viene de la API con ese código concreto. */
export function hasErrorCode(error: unknown, code: string): boolean {
  return error instanceof ApiClientError && error.code === code;
}

export { ApiClientError, NetworkError };
